import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Staff from "@/pages/Staff";
import Certifications from "@/pages/Certifications";
import Locations from "@/pages/Locations";
import Shifts from "@/pages/Shifts";
import Timesheets from "@/pages/Timesheets";
import ClockIn from "@/pages/ClockIn";
import Incidents from "@/pages/Incidents";
import MAR from "@/pages/MAR";
import Notifications from "@/pages/Notifications";
import Audit from "@/pages/Audit";
import Finance from "@/pages/Finance";
import Invoices from "@/pages/Invoices";
import OpenShifts from "@/pages/OpenShifts";
import Training from "@/pages/Training";
import AuditLinks from "@/pages/AuditLinks";
import AuditView from "@/pages/AuditView";
import Devices from "@/pages/Devices";
import ReviewAlert from "@/pages/ReviewAlert";
import Individuals from "@/pages/Individuals";
import ReviewQueue from "@/pages/ReviewQueue";

function Shell({ children }) {
  return <Layout>{children}</Layout>;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public inspector view — token IS the auth, no login wrapper */}
          <Route path="/audit" element={<AuditView />} />
          {/* Public review-alert page — token validates read-only; confirm POST
              requires auth and is handled in-page, so NOT inside ProtectedRoute */}
          <Route path="/account/devices/review-alert" element={<ReviewAlert />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Shell>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/staff" element={<RoleRoute roles={["admin"]}><Staff /></RoleRoute>} />
                    <Route path="/individuals" element={<RoleRoute roles={["admin","rn","dsp"]}><Individuals /></RoleRoute>} />
                    <Route path="/review-queue" element={<RoleRoute roles={["admin","rn"]}><ReviewQueue /></RoleRoute>} />
                    <Route path="/certifications" element={<Certifications />} />
                    <Route path="/locations" element={<RoleRoute roles={["admin"]}><Locations /></RoleRoute>} />
                    <Route path="/shifts" element={<Shifts />} />
                    <Route path="/my-schedule" element={<Shifts />} />
                    <Route path="/timesheets" element={<Timesheets />} />
                    <Route path="/clock" element={<RoleRoute roles={["dsp"]}><ClockIn /></RoleRoute>} />
                    <Route path="/incidents" element={<Incidents />} />
                    <Route path="/mar" element={<MAR />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/audit-log" element={<RoleRoute roles={["admin"]}><Audit /></RoleRoute>} />
                    <Route path="/finance" element={<RoleRoute roles={["admin"]}><Finance /></RoleRoute>} />
                    <Route path="/invoices" element={<RoleRoute roles={["admin"]}><Invoices /></RoleRoute>} />
                    <Route path="/open-shifts" element={<OpenShifts />} />
                    <Route path="/training" element={<Training />} />
                    <Route path="/audit-links" element={<RoleRoute roles={["admin"]}><AuditLinks /></RoleRoute>} />
                    <Route path="/account/devices" element={<Devices />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Shell>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
